import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Briefcase, UsersThree, TrendUp, CheckCircle, ArrowRight, UsersFour, UserCheck, UserMinus } from "@phosphor-icons/react";
import BottleneckSpotlight from "@/components/BottleneckSpotlight";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];
const STAGE_COLORS = {
    Applied: "bg-[#F5F5F5] text-[#525252]",
    Screening: "bg-[#EEF2FF] text-[#3730A3]",
    Interview: "bg-[#EFF6FF] text-[#F59E0B]",
    Offer: "bg-[#FAF5FF] text-[#7E22CE]",
    Hired: "bg-[#ECFDF5] text-[#059669]",
    Rejected: "bg-[#FEF2F2] text-[#DC2626]",
};

function Metric({ label, value, icon: Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className="bg-white border border-[#E5E5E5] p-4 sm:p-6 flex flex-col gap-2 sm:gap-3"
        >
            <div className="flex items-center justify-between">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] leading-tight">{label}</p>
                <Icon size={18} weight="bold" className="text-[#A3A3A3] shrink-0" />
            </div>
            <p className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink">{value}</p>
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [jobs, setJobs] = useState([]);
    // Recruiter-only toggle: "mine" (own candidates) | "all" (own + team pool)
    const [scopeMode, setScopeMode] = useState("mine");

    const fetchStats = useCallback((mode) => {
        api.get(`/stats?scope_mode=${mode}`).then((r) => setStats(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        fetchStats(scopeMode);
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    }, [scopeMode, fetchStats]);

    const totalActive = stats
        ? (stats.pipeline.Applied || 0) +
          (stats.pipeline.Screening || 0) +
          (stats.pipeline.Interview || 0) +
          (stats.pipeline.Offer || 0)
        : 0;

    const isRecruiter = stats?.role === "recruiter";
    const teamPoolCount = stats?.team_pool_count ?? 0;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] w-full min-w-0 mx-auto" data-testid="dashboard-page" style={{ maxWidth: "min(1600px, 100vw)" }}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 sm:mb-10">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Overview / Control Room
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink break-words">
                        Dashboard.
                    </h1>
                </div>
                <Link
                    to="/requirements"
                    data-testid="dashboard-new-job-link"
                    className="btn-gradient self-start md:self-auto shrink-0 px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2"
                >
                    New requirement <ArrowRight size={14} weight="bold" />
                </Link>
            </div>

            {/* Recruiter scope toggle */}
            {isRecruiter && (
                <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="inline-flex bg-white border border-[#E5E5E5] p-1" data-testid="scope-toggle">
                        <button
                            data-testid="scope-mine-btn"
                            onClick={() => setScopeMode("mine")}
                            className={`text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors ${
                                scopeMode === "mine" ? "bg-ink text-white" : "text-[#525252] hover:bg-[#F5F5F5]"
                            }`}
                        >
                            My candidates
                        </button>
                        <button
                            data-testid="scope-all-btn"
                            onClick={() => setScopeMode("all")}
                            className={`text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors ${
                                scopeMode === "all" ? "bg-ink text-white" : "text-[#525252] hover:bg-[#F5F5F5]"
                            }`}
                        >
                            + Team pool
                        </button>
                    </div>
                    {teamPoolCount > 0 && (
                        <div
                            data-testid="team-pool-chip"
                            className="inline-flex items-center gap-2 bg-[#EEF2FF] border border-[#C7D2FE] px-3 py-1.5"
                            title="Candidates who self-applied via the portal — any recruiter on the job can claim them."
                        >
                            <UsersFour size={14} weight="bold" className="text-[#3730A3]" />
                            <span className="text-xs font-bold text-[#3730A3]">
                                {teamPoolCount} in team pool
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-5">
                <Metric
                    label="Open requirements"
                    value={stats?.open_jobs ?? "—"}
                    icon={Briefcase}
                    testid="metric-open-jobs"
                />
                <Metric
                    label={isRecruiter && scopeMode === "mine" ? "My candidates" : "Total candidates"}
                    value={stats?.total_candidates ?? "—"}
                    icon={UsersThree}
                    testid="metric-total-candidates"
                />
                <Metric
                    label="In active pipeline"
                    value={totalActive}
                    icon={TrendUp}
                    testid="metric-active-pipeline"
                />
                <Metric
                    label="Hired"
                    value={stats?.hired ?? "—"}
                    icon={CheckCircle}
                    testid="metric-hired"
                />
            </div>

            {/* V1.5.8.23 — Registration KPIs. Recruiter → own scope,
                Admin/HM/Superadmin → workspace-wide (backend `/stats`
                already honours the same scope split). */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10">
                <Metric
                    label="Registration done"
                    value={stats?.registration_done ?? "—"}
                    icon={UserCheck}
                    testid="metric-registration-done"
                />
                <Metric
                    label="Registration pending"
                    value={stats?.registration_pending ?? "—"}
                    icon={UserMinus}
                    testid="metric-registration-pending"
                />
            </div>

            {/* Pipeline distribution */}
            <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-8 sm:mb-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-4">
                    Pipeline distribution
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                    {STAGES.map((s) => (
                        <div key={s} className="border border-[#E5E5E5] p-3">
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${STAGE_COLORS[s]}`}>
                                {s}
                            </span>
                            <p className="font-heading font-black text-3xl tracking-tighter mt-3 text-ink">
                                {stats?.pipeline?.[s] ?? 0}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottleneck spotlight — surfaces the slowest funnel stage (Feb 2026) */}
            <BottleneckSpotlight />

            {/* Recent jobs */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Recent requirements</h2>
                <Link to="/requirements" className="text-sm font-bold text-cobalt hover:underline">
                    View all →
                </Link>
            </div>
            <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
                {jobs.length === 0 && (
                    <p className="p-6 text-sm text-[#525252]" data-testid="no-jobs-empty">
                        No requirements yet. Create your first one.
                    </p>
                )}
                {jobs.slice(0, 5).map((j) => (
                    <Link
                        key={j.id}
                        to={`/requirements/${j.id}`}
                        data-testid={`recent-job-${j.id}`}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 p-4 sm:p-5 hover:bg-[#F9F9F9] transition-colors"
                    >
                        <div className="min-w-0">
                            <p className="font-bold text-ink truncate">{j.title}</p>
                            <p className="text-sm text-[#525252] truncate">
                                {j.department || "—"} · {j.location || "Remote"} · {j.seniority}
                            </p>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#525252]">
                                {j.candidate_count} candidates
                            </span>
                            <span
                                className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${
                                    j.status === "Open"
                                        ? "bg-[#ECFDF5] text-[#059669]"
                                        : "bg-[#F5F5F5] text-[#525252]"
                                }`}
                            >
                                {j.status}
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
